import { useEffect } from 'react';
import {
  readReinitiatedWizardMappingHandoffIntent,
  readResumeIntoMappingIntent,
} from '../../../shared/lib/workflowResumeIntent';
import {
  useLegacyProjectSetupClaimed,
  useProjectSetupCompleted,
  useProjectSetupHistoryStore,
} from '../../../shared/stores/useProjectSetupHistoryStore';
import type { Screen } from '../../../shared/types';
import { readMappingProjectProfileBinding } from '../lib/mappingProfileProjectTrust';
import { shouldResumeMappingAtStartup } from '../lib/mappingStartupResumePolicy';

interface UseMappingStartupResumeInput {
  authSatisfied: boolean;
  activeUserId: string | null;
  activeProjectKey: string | null;
  currentScreen: Screen;
  navigateTo: (screen: Screen) => void;
}

export function useMappingStartupResume({
  authSatisfied,
  activeUserId,
  activeProjectKey,
  currentScreen,
  navigateTo,
}: UseMappingStartupResumeInput): void {
  const projectSetupCompleted = useProjectSetupCompleted(activeUserId, activeProjectKey);
  const legacyProjectSetupClaimed = useLegacyProjectSetupClaimed(activeProjectKey);
  const claimLegacyProjectSetupCompletion = useProjectSetupHistoryStore(
    (state) => state.claimLegacyCompletion
  );

  useEffect(() => {
    const reinitiatedWizard = readReinitiatedWizardMappingHandoffIntent();
    const resumeIntoMappingRequested = readResumeIntoMappingIntent();
    const hasLegacyProjectSetupEvidence =
      !projectSetupCompleted &&
      !legacyProjectSetupClaimed &&
      !reinitiatedWizard &&
      Boolean(activeUserId) &&
      readMappingProjectProfileBinding(activeUserId, activeProjectKey)?.setupCompleted === true;
    if (hasLegacyProjectSetupEvidence) {
      claimLegacyProjectSetupCompletion(activeUserId, activeProjectKey);
    }

    const resumeRequested = shouldResumeMappingAtStartup({
      authSatisfied,
      currentScreen,
      projectSetupCompleted: projectSetupCompleted || hasLegacyProjectSetupEvidence,
      resumeIntoMappingRequested,
      reinitiatedWizard,
    });

    if (!resumeRequested) {
      return;
    }

    navigateTo('mappingConfiguration');
  }, [
    activeProjectKey,
    activeUserId,
    authSatisfied,
    claimLegacyProjectSetupCompletion,
    currentScreen,
    legacyProjectSetupClaimed,
    navigateTo,
    projectSetupCompleted,
  ]);
}
